import {useState, useMemo} from 'react';
import {createFileRoute, Link, redirect, useRouter} from '@tanstack/react-router';
import {
  ConnectAccountOnboarding,
  ConnectComponentsProvider,
} from '@stripe/react-connect-js';
import {loadConnectAndInitialize} from '@stripe/connect-js';
import {createConnectedAccount, createAccountSession} from '@/server/stripe';
import {updateOrganizer} from '@/server/organizer';
import {getOrganizerById} from '@/server/organizer';
import {Logo} from '@/components/ui/logo/logo';
import {Card, CardContent} from '@/components/ui/card';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Typography} from '@/components/ui/typography';
import {Spinner} from '@/components/ui/spinner';

export const Route = createFileRoute('/admin/connect/$organizerId')({
  beforeLoad: async ({context, location}) => {
    if (!context.profile) {
      throw redirect({
        to: '/login',
        search: {redirect: location.href},
      });
    }
  },
  loader: async ({params}) => {
    const organizer = await getOrganizerById({
      data: params.organizerId,
    });

    // If no contact email, show email prompt before creating account
    if (!organizer.contactEmail) {
      return {
        needsEmail: true,
        clientSecret: null,
        organizerId: params.organizerId,
        organizer,
      };
    }

    await createConnectedAccount({
      data: {organizerId: params.organizerId},
    });

    const {clientSecret} = await createAccountSession({
      data: {organizerId: params.organizerId},
    });

    return {
      needsEmail: false,
      clientSecret,
      organizerId: params.organizerId,
      organizer,
    };
  },
  pendingMs: 0,
  pendingComponent: ConnectLoadingPage,
  component: ConnectOnboardingPage,
  errorComponent: ConnectOnboardingError,
});

/** Loading state shown immediately while the Stripe account and session are created. */
function ConnectLoadingPage() {
  return (
    <div className="flex min-h-screen flex-col items-center px-4">
      <Logo className="h-8 mt-8" />
      <div className="flex flex-1 w-full max-w-2xl flex-col items-center gap-4 p-12">
        <Spinner />
        <p className="text-sm text-gray-500">
          Setting up your payment account…
        </p>
      </div>
    </div>
  );
}

/**
 * Error fallback shown when the loader fails to create
 * a connected account or fetch the account session.
 */
function ConnectOnboardingError({error}: {error: Error}) {
  return (
    <div className="flex min-h-screen flex-col items-center px-4">
      <Logo className="h-8 mt-8" />
      <div className="flex flex-1 w-full items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 text-center">
            <h2 className="text-lg font-semibold">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">
              {error.message || 'Unable to load onboarding.'}
            </p>
            <Link to="/dashboard">
              <Button variant="outline">Back to Dashboard</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface ContactEmailPromptProps {
  organizer: {
    id: string;
    name: string;
    description: string | null;
    website: string | null;
    contactEmail: string | null;
    logoUrl: string | null;
  };
  onSaved: () => void;
}

/**
 * Prompts the organizer admin for a contact email before proceeding
 * with Stripe Connect onboarding. Stripe requires a contact email
 * on accounts with recipient configuration.
 */
function ContactEmailPrompt({organizer, onSaved}: ContactEmailPromptProps) {
  const [email, setEmail] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim();
    if (!trimmed) return;

    setIsSaving(true);
    setError(null);

    try {
      await updateOrganizer({
        data: {
          id: organizer.id,
          name: organizer.name,
          description: organizer.description,
          website: organizer.website,
          contactEmail: trimmed,
          logoUrl: organizer.logoUrl,
        },
      });
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save email');
      setIsSaving(false);
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center px-4">
      <Logo className="h-8 mt-8" />
      <div className="flex flex-1 w-full items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col gap-4">
            <Typography level="H4">Contact Email Required</Typography>
            <Typography level="B2" className="text-neutral-600">
              Stripe requires a contact email to set up payments for{' '}
              {organizer.name}.
            </Typography>
            <form onSubmit={handleSubmit} className="flex flex-col gap-3">
              <Input
                type="email"
                placeholder="organizer@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {error && (
                <Typography level="B3" className="text-error-700">
                  {error}
                </Typography>
              )}
              <Button type="submit" disabled={isSaving || !email.trim()}>
                {isSaving ? 'Saving...' : 'Continue'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

/**
 * Reads a CSS custom property from :root and returns its computed value.
 * The browser resolves oklch/var() references to rgb, which Stripe accepts.
 */
function cssVar(name: string): string {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(name)
    .trim();
}

/**
 * Converts the CSS --radius value (rem) to a pixel string for Stripe,
 * which only accepts pixel values for border radius.
 */
function radiusPx(): string {
  const rem = parseFloat(cssVar('--radius'));
  const rootFontSize = parseFloat(
    getComputedStyle(document.documentElement).fontSize
  );
  return `${Math.round(rem * rootFontSize)}px`;
}

/**
 * Standalone Stripe Connect onboarding page. Renders the embedded
 * ConnectAccountOnboarding component so organizers can complete
 * identity verification and payment setup without leaving the app.
 * Prompts for a contact email first if the organizer doesn't have one.
 */
function ConnectOnboardingPage() {
  const {clientSecret, organizerId, needsEmail, organizer} = Route.useLoaderData();
  const router = useRouter();

  // useMemo so the instance re-initializes when clientSecret becomes available
  const stripeInstance = useMemo(() => {
    if (!clientSecret) return null;
    if (typeof window === 'undefined') return null;
    if (!import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY) {
      throw new Error('VITE_STRIPE_PUBLISHABLE_KEY is not set');
    }

    const primary = cssVar('--color-primary');
    const background = cssVar('--background');
    const foreground = cssVar('--foreground');
    const destructive = cssVar('--destructive');
    const primaryForeground = cssVar('--primary-foreground');
    const radius = radiusPx();

    return loadConnectAndInitialize({
      publishableKey: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
      fetchClientSecret: async () => clientSecret,
      appearance: {
        overlays: 'dialog',
        variables: {
          fontFamily: 'Satoshi-Regular, sans-serif',
          colorPrimary: primary,
          colorBackground: background,
          colorText: foreground,
          colorDanger: destructive,
          borderRadius: radius,
          buttonBorderRadius: radius,
          formBorderRadius: radius,
          spacingUnit: '10px',
          buttonPrimaryColorBackground: primary,
          buttonPrimaryColorBorder: primary,
          buttonPrimaryColorText: primaryForeground,
          formAccentColor: primary,
          actionPrimaryColorText: primary,
        },
      },
    });
  }, [clientSecret]);

  if (needsEmail) {
    return (
      <ContactEmailPrompt
        organizer={organizer}
        onSaved={() => router.invalidate()}
      />
    );
  }

  if (!clientSecret) return null;

  if (!stripeInstance) return null;

  return (
    <div className="flex min-h-screen flex-col items-center px-4">
      <Logo className="h-8 mt-8" />
      <div className="flex flex-1 w-full max-w-2xl justify-center">
        <ConnectComponentsProvider connectInstance={stripeInstance}>
          <ConnectAccountOnboarding
            onExit={() => {
              window.location.href = `/admin/dashboard/organizer/${organizerId}`;
            }}
          />
        </ConnectComponentsProvider>
      </div>
    </div>
  );
}
